import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { listarEquipeObra } from "./equipe-obra.gateway";
import type { EquipeObraProfissional, EquipeObraResumo } from "./equipe-obra.types";

export const EQUIPE_OBRA_LISTA_FORBIDDEN_MESSAGE = "Acesso não autorizado";
export const EQUIPE_OBRA_LISTA_ERROR_MESSAGE = "Não foi possível carregar a equipe da obra.";

export interface EquipeObraQueryState {
  status: "loading" | "success" | "error" | "forbidden";
  obra: EquipeObraResumo | null;
  alocacoes: EquipeObraProfissional[];
  errorMessage: string | null;
}

const INITIAL_STATE: EquipeObraQueryState = {
  status: "loading",
  obra: null,
  alocacoes: [],
  errorMessage: null,
};

/** Equipe já gravada da obra (HU 2051). */
export function useEquipeObra(obraId: string, { enabled = true }: { enabled?: boolean } = {}) {
  const [state, setState] = useState<EquipeObraQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    setState(INITIAL_STATE);

    listarEquipeObra(obraId, controller.signal)
      .then(({ obra, alocacoes }) => {
        if (cancelled) return;
        setState({ status: "success", obra, alocacoes, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;

        const forbidden = isAPIError(error) && error.status === 403;
        setState({
          status: forbidden ? "forbidden" : "error",
          obra: null,
          alocacoes: [],
          errorMessage: forbidden
            ? EQUIPE_OBRA_LISTA_FORBIDDEN_MESSAGE
            : getErrorMessage(error) || EQUIPE_OBRA_LISTA_ERROR_MESSAGE,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [obraId, reloadToken, enabled]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  /** Troca localmente a alocação devolvida pelo PATCH, sem nova consulta. */
  const replaceAlocacao = useCallback((alocacao: EquipeObraProfissional) => {
    setState((current) => ({
      ...current,
      alocacoes: current.alocacoes.map((item) => (item.id === alocacao.id ? alocacao : item)),
    }));
  }, []);

  return { ...state, reload, replaceAlocacao };
}
